/**
 * Tell the backend the voter pressed Stop.
 *
 * A turn keeps running after its stream closes, so an app sent to the
 * background still gets its answer (see `turnRecovery.ts`). That made closing
 * the stream a poor way to say Stop: the backend finished the answer anyway,
 * and the stopped response came back complete the next time the conversation
 * opened (staging QA, September 2026). Stop is now its own request, carrying how
 * much of the answer was on screen so the saved turn matches what the voter saw.
 * Identical in both clients (`src/services/turnStop.ts`).
 */

export interface TurnStop {
  turnId: string;
  conversationId: string;
  /** The answer text on screen when the voter stopped, as a JS string length. */
  shownChars: number;
}

/** POSTs `body` to `path`; rejects when the request fails. */
export type PostJson = (path: string, body: Record<string, unknown>) => Promise<unknown>;

export function turnStopPath(turnId: string): string {
  return `/chat/turn/${encodeURIComponent(turnId)}/cancel`;
}

export function turnStopBody(stop: TurnStop): Record<string, unknown> {
  return {
    conversation_id: stop.conversationId,
    shown_chars: Math.max(0, Math.floor(stop.shownChars)),
  };
}

/** How long a failed Stop waits before it is sent again. */
export const STOP_RETRY_DELAY_MS = 1_000;

/**
 * Send the Stop, retrying once after a pause: a network hiccup at the moment
 * of the tap, or a backend that could not record the Stop (it answers 503),
 * would otherwise leave the turn running to completion.
 *
 * @returns Whether the backend accepted it.
 */
export async function sendTurnStop(
  stop: TurnStop,
  post: PostJson,
  attempts = 2,
  retryDelayMs = STOP_RETRY_DELAY_MS
): Promise<boolean> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    try {
      await post(turnStopPath(stop.turnId), turnStopBody(stop));
      return true;
    } catch {
      // Retried below; the local UI has already settled the turn as stopped.
    }
  }
  return false;
}

/**
 * How long a Stop pressed before the `start` frame arrived keeps reading the
 * stream for it. The frame is the first thing the backend sends, normally
 * well under a second in; this bounds a stream that never produces one.
 */
export const STOP_AWAIT_START_MS = 15_000;
